/* global deptRaidCanManageRisks deptRaidListUsersUrl */
/* global deptRaidFetchGeneralRisksUrl deptRaidStoreGeneralRiskUrl deptRaidUpdateGeneralRiskUrl deptRaidFetchGeneralRiskUrl */
/* global deptRaidFetchGeneralRiskTypesUrl deptRaidFetchGeneralRiskImpactsUrl deptRaidFetchGeneralImpactCategoriesUrl */
/* global deptRaidFetchGeneralRiskProbabilitiesUrl deptRaidFetchGeneralRiskStatusesUrl deptRaidGetGeneralRiskPriorityUrl */
/* global deptRaidFetchGeneralRiskBudgetLinesUrl deptRaidFetchEventDepartmentsOptionsUrl */

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { EDIT_GENERAL_RISK_EVENT, SOURCE_TYPE_DEPARTMENT } from '../radio/constants';
import { RaidProvider, useRaidContext } from '../radio/context/RaidContext';
import { UPSERT_RISK_MODAL_ID } from '../radio/register/general_risk/GeneralRisksIndex';
import GeneralRiskUpsertModal from '../radio/register/general_risk/GeneralRiskUpsertModal';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';
import { UsersProvider } from 'Context/UsersContext';

import useModal from 'Hooks/useModal';

/**
 * The department page's own risk editor. A risk escalated to the general
 * register used to be edited by navigating to the central register, which a
 * user restricted to selected departments is refused — so the modal is hosted
 * here instead, opened in place by the delegated trigger on the risk table.
 *
 * The register's own tables stay where they are: this island contributes the
 * modal only, so nothing on the page is rendered twice.
 */
export default function DeptRaidApp() {
    const {
        risks,
        types,
        impacts,
        impactCategories,
        probabilities,
        statuses,
        departmentsOptions,
        canManageRisks,
        currentBudgetDepartment,
        loadingDatasets,
        fetchGeneralRisks,
        routes,
        selectedRisk,
        setSelectedRisk,
        modalsVisibility,
        toggleModalVisibility,
    } = useRaidContext();

    const [isLoading, setIsLoading] = useState(false);
    const riskModal = useModal();

    useEffect(() => {
        setIsLoading(true);
        fetchGeneralRisks()
            .catch((e) => console.error(e))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (modalsVisibility[UPSERT_RISK_MODAL_ID]) {
            riskModal.show();
        } else {
            riskModal.hide();
        }
    }, [modalsVisibility[UPSERT_RISK_MODAL_ID]]);

    const openRisk = useCallback(
        (generalRiskId) => {
            if (!canManageRisks) {
                return;
            }

            // No id means an "add" affordance was clicked, so the form opens empty.
            const risk = generalRiskId ? risks.find((r) => r.id === parseInt(generalRiskId, 10)) : null;

            // A known id that is not in the fetched list belongs to another
            // department: leave the modal shut rather than opening it blank.
            if (generalRiskId && !risk) {
                return;
            }

            setSelectedRisk(risk);
            toggleModalVisibility(UPSERT_RISK_MODAL_ID, true);
        },
        [risks, canManageRisks, setSelectedRisk, toggleModalVisibility],
    );

    useEffect(() => {
        const handler = (event) => openRisk(event.detail?.generalRiskId);

        document.addEventListener(EDIT_GENERAL_RISK_EVENT, handler);

        return () => document.removeEventListener(EDIT_GENERAL_RISK_EVENT, handler);
    }, [openRisk]);

    const onClose = () => toggleModalVisibility(UPSERT_RISK_MODAL_ID, false);

    const onCreated = () => {
        fetchGeneralRisks().catch((e) => console.error(e));
        // The risk tables on this page are server-rendered, so their rows only
        // pick up the change on the next load.
        window.location.reload();
    };

    return (
        <GeneralRiskUpsertModal
            id={UPSERT_RISK_MODAL_ID}
            isLoading={loadingDatasets || isLoading}
            allowVenueSelection={false}
            datasets={{ types, impacts, impactCategories, probabilities, statuses, issues: [], decisions: [], departmentsOptions, venuesOptions: [] }}
            getPriorityUrl={routes.getGeneralRiskPriorityUrl}
            storeUrl={routes.storeGeneralRiskUrl}
            updateUrl={routes.updateGeneralRiskUrl}
            fetchGeneralRiskBudgetLinesUrl={routes.fetchGeneralRiskBudgetLinesUrl}
            modal={riskModal}
            risk={selectedRisk}
            budget_department_type={currentBudgetDepartment?.type}
            budget_department_id={currentBudgetDepartment?.id}
            onCreated={onCreated}
            onClose={onClose}
            canManage={canManageRisks}
            onDelete={onClose}
        />
    );
}

export const EDIT_TRIGGER_SELECTOR = '[data-radio-edit-general-risk]';

if (document.getElementById('DeptRaidApp')) {
    const root = document.getElementById('DeptRaidApp');
    const budgetDepartment = JSON.parse(root.dataset.budgetDepartment);

    // Delegated from `document`: the risk rows carrying these triggers are drawn
    // by the report tables after page load, so a direct binding would attach to
    // nothing. Left to bubble — the surrounding row has its own click handling.
    document.addEventListener('click', (event) => {
        // Not every click target is an Element that can be walked upwards.
        const trigger = event.target instanceof Element ? event.target.closest(EDIT_TRIGGER_SELECTOR) : null;

        if (!trigger) {
            return;
        }

        event.preventDefault();

        document.dispatchEvent(
            new CustomEvent(EDIT_GENERAL_RISK_EVENT, {
                detail: { generalRiskId: trigger.dataset.radioEditGeneralRisk || null },
            }),
        );
    });

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <UsersProvider fetchUrl={deptRaidListUsersUrl}>
                    <RaidProvider
                        source={SOURCE_TYPE_DEPARTMENT}
                        budgetDepartment={budgetDepartment}
                        canManageRisks={deptRaidCanManageRisks}
                        canManageIssues={false}
                        canManageDecisions={false}
                        routes={{
                            fetchGeneralRisksUrl: deptRaidFetchGeneralRisksUrl,
                            fetchGeneralRiskUrl: deptRaidFetchGeneralRiskUrl,
                            storeGeneralRiskUrl: deptRaidStoreGeneralRiskUrl,
                            updateGeneralRiskUrl: deptRaidUpdateGeneralRiskUrl,
                            fetchGeneralRiskTypesUrl: deptRaidFetchGeneralRiskTypesUrl,
                            fetchGeneralRiskImpactsUrl: deptRaidFetchGeneralRiskImpactsUrl,
                            fetchGeneralImpactCategoriesUrl: deptRaidFetchGeneralImpactCategoriesUrl,
                            fetchGeneralRiskProbabilitiesUrl: deptRaidFetchGeneralRiskProbabilitiesUrl,
                            fetchGeneralRiskStatusesUrl: deptRaidFetchGeneralRiskStatusesUrl,
                            getGeneralRiskPriorityUrl: deptRaidGetGeneralRiskPriorityUrl,
                            fetchGeneralRiskBudgetLinesUrl: deptRaidFetchGeneralRiskBudgetLinesUrl,
                            fetchEventDepartmentsOptionsUrl: deptRaidFetchEventDepartmentsOptionsUrl,
                        }}
                    >
                        <DeptRaidApp />
                    </RaidProvider>
                </UsersProvider>
            </ConfirmationModalProvider>
        </App>,
        root,
    );
}
